import { useCallback, useEffect, useRef, type RefObject } from 'react'
import { createDynamicInterval } from '../utils/createDynamicInterval'

export type TWorldPositionAxisEntity = { x: number; y: number; zoom: number }

type TParams<Element extends HTMLElement> = {
  ignoreMouseDownClassName?: string
  rotationAreaRef?: RefObject<Element>
  mode: 'relative-offset' | 'relative-window'
  onMove?: (data: TWorldPositionAxisEntity) => void
}

const dynamicInterval = createDynamicInterval()

export const useClientCameraRotation = <Element extends HTMLElement>({
  rotationAreaRef,
  ignoreMouseDownClassName,
  mode,
  onMove,
}: TParams<Element>) => {
  const previousPosition = useRef<TWorldPositionAxisEntity | null>(null)

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      if (
        ignoreMouseDownClassName !== undefined &&
        (event.target as HTMLElement)?.closest(`.${ignoreMouseDownClassName}`)
      )
        return

      previousPosition.current = {
        x: event.clientX,
        y: event.clientY,
        zoom: 0,
      }

      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    },
    [ignoreMouseDownClassName]
  )

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      dynamicInterval(() => {
        if (previousPosition.current === null) return

        if (
          previousPosition.current.x === event.clientX &&
          previousPosition.current.y === event.clientY
        ) {
          dynamicInterval.stop()
          return
        }

        if (mode === 'relative-offset') {
          onMove?.({
            x: event.clientX - previousPosition.current.x,
            y: event.clientY - previousPosition.current.y,
            zoom: 0,
          })
        } else if (mode === 'relative-window') {
          onMove?.({
            x: event.clientX,
            y: event.clientY,
            zoom: 0,
          })
        }

        previousPosition.current = {
          x: event.clientX,
          y: event.clientY,
          zoom: 0,
        }
      }, 10)
    },
    [onMove, mode]
  )

  const handleMouseUp = useCallback(() => {
    dynamicInterval.stop()
    previousPosition.current = null
    window.removeEventListener('mousemove', handleMouseMove)
    window.removeEventListener('mouseup', handleMouseUp)
  }, [])

  const handleWheel = useCallback(
    (event: Event) => {
      const wheelEvent = event as WheelEvent;
      if (!onMove) return

      wheelEvent.preventDefault()

      onMove?.({ x: 0, y: 0, zoom: wheelEvent.deltaY })
    },
    [onMove]
  )

  useEffect(() => {
    const target = rotationAreaRef?.current ?? window

    target.addEventListener('mousedown', handleMouseDown as EventListener)
    target.addEventListener('wheel', handleWheel as EventListener, { passive: false })

    return () => {
      target.removeEventListener('mousedown', handleMouseDown as EventListener)
      target.removeEventListener('wheel', handleWheel as EventListener)
    }
  }, [handleMouseDown, handleWheel, rotationAreaRef])
}
